import type { LinksFunction } from "react-router";
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router";
import { clerkMiddleware, rootAuthLoader } from "@clerk/react-router/server";
import { ClerkProvider } from "@clerk/react-router";
import { Header } from "./components/ui/Header";
import { Footer } from "./components/ui/Footer";
import "./tailwind.css";
import { Route } from "./+types/root";
import * as Sentry from "@sentry/react-router";
import { ErrorBoundary as ErrorBoundaryComponent } from "./components/ErrorBoundary";
import type { ReactNode } from "react";

export const links: LinksFunction = () => [];

export const middleware: Route.MiddlewareFunction[] = [clerkMiddleware()];

export async function loader(args: Route.LoaderArgs) {
  return rootAuthLoader(args);
}

export function Layout({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App({ loaderData }: Route.ComponentProps) {
  // Allow localhost for development
  const isLocalhost =
    typeof window !== "undefined" && window.location.hostname === "localhost";
  const domain = isLocalhost ? undefined : "servicedesk.rb2.nl";

  return (
    <ClerkProvider
      loaderData={loaderData}
      signUpFallbackRedirectUrl="/"
      signInFallbackRedirectUrl="/"
      domain={domain}
    >
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="grow">
          <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
        <Footer />
      </div>
    </ClerkProvider>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // Log error for monitoring
  console.error("Application error:", error);

  // Capture exception in Sentry for production monitoring
  if (error instanceof Error) {
    Sentry.captureException(error);
  }

  // Use the new error boundary component
  return <ErrorBoundaryComponent />;
}
